'use strict';

import $ from 'jquery';
import cookies from 'js-cookie';
import _ from 'underscore';
import config from '../../js/config';
import utilities from '../../js/utilities';
import mediator from '../../js/mediator';
import $global from '../../js/globalElements';
import { EnableAuthenticatedMarketingTracking, EnableB2BUsersApiCall, EnableAdobeAnalytics } from '../../utilities/featureFlags';
import cartApi from '../../services/CartAPI';

const socialShareClassMapping = [
  {
    className: 'Icon--facebook',
    shareChannel: 'Facebook'
  },
  {
    className: 'Icon--twitter',
    shareChannel: 'Twitter'
  },
  {
    className: 'Icon--google-plus',
    shareChannel: 'Google+'
  },
  {
    className: 'Icon--linkedin',
    shareChannel: 'LinkedIn'
  },
  {
    className: 'Icon--pinterest',
    shareChannel: 'Pinterest'
  }
];

export default class MarketingTracking {
  constructor() {
    this.initializeCommonDataTracking();
    this.initializeUserRegistrationTracking();
    this.initializeUserLoginTracking();
    this.initializeAddToCartTracking();
    this.initializeSocialShareTracking();
    this.initializeDatasheetEventTracking();
    this.initializeReferenceDesignTracking();
    this.initializeSaveToMyProjectTracking();
    this.initializeManufacturerTracking();
  }

  isUserAuthenticated() {
    const { isAuthorized = false } = window.Arrow || {};
    return isAuthorized;
  }

  isSubscribedForAuthenticatedTracking() {
    if (EnableAuthenticatedMarketingTracking.isEnabled()) {
      return this.isUserAuthenticated();
    }
    return true;
  }

  initializeCommonDataTracking() {
    mediator.on(config.events.cart.userInfoSet, this.userInfo, this);
  }

  initializeUserRegistrationTracking() {
    let userRegisteredValue = sessionStorage.getItem(config.sessionVariables.marketingTracking.userJustTriedToRegisterCookie);
    if (userRegisteredValue) {
      let userRegisteredValueSerialized = JSON.parse(userRegisteredValue);
      sessionStorage.removeItem(config.sessionVariables.marketingTracking.userJustTriedToRegisterCookie);
      let userData = this.collectUserData(userRegisteredValueSerialized.email);
      userData.firstName = userRegisteredValueSerialized.firstName;
      userData.lastName = userRegisteredValueSerialized.lastName;
      userData.optIn = userRegisteredValueSerialized.optIn;
      userData.privacyTermsOptIn = userRegisteredValueSerialized.privacyTermsOptIn;
      if (userRegisteredValueSerialized.country) {
        userData.country = userRegisteredValueSerialized.country;
      }
      this.track(config.urls.marketingTracking.trackRegistration, userData);
    }
  }

  initializeUserLoginTracking() {
    let userLoggedInValue = sessionStorage.getItem(config.sessionVariables.marketingTracking.userJustLoggedInCookie);
    if (userLoggedInValue) {
      sessionStorage.removeItem(config.sessionVariables.marketingTracking.userJustLoggedInCookie);
      if (this.isUserAuthenticated()) {
        let userData = this.collectUserData(userLoggedInValue);
        this.track(config.urls.marketingTracking.trackLogin, userData);
      }
    }
  }

  initializeAddToCartTracking() {
    mediator.on(config.events.cart.added, this.onAddToCart.bind(this));
  }

  initializeSocialShareTracking() {
    mediator.on(config.events.share.modal, this.onSocialShare.bind(this));
  }

  initializeDatasheetEventTracking() {
    mediator.on(config.events.datasheets.download, this.onDatasheetsEvent.bind(this));
    mediator.on(config.events.datasheets.view, this.onDatasheetsEvent.bind(this));
  }

  initializeReferenceDesignTracking() {
    mediator.on(config.events.referenceDesigns.download, this.onReferenceDesignsEvent.bind(this));
    mediator.on(config.events.referenceDesigns.view, this.onReferenceDesignsEvent.bind(this));
  }

  initializeSaveToMyProjectTracking() {
    this.postDataSaveToMyProjects = {};
    $global.$body.on('click', '.saveTo', _.bind(this.onSaveToClick, this));
    mediator.on(config.events.designCenter.syncDocumentProjects, this.syncDocumentProjects, this);
    mediator.on(config.events.designCenter.syncProductProjects, this.syncProductProjects, this);
  }

  initializeManufacturerTracking() {
    mediator.on(config.events.manufacturer.viewPage, this.onManufacturerEvent.bind(this));
  }

  splitKeyValue(str) {
    let result = {};
    if (str) {
      str.split('\n').forEach(function(x) {
        let arr = x.split('=');
        arr[1] && (result[arr[0]] = arr[1]);
      });
    }
    return result
  }

  userInfo(data) {
    this.userInfoData = data;
  }

  readMultipleCookies(name) {
    return document.cookie.split(';').map(function(cookiestring) {
      let cs = cookiestring.trim().split('=');
      if (cs.length === 2) {
        return {'name': cs[0], 'value': cs[1]};
      } else {
        return {'name': '', 'value': ''};
      }
    })
      .filter(function(cookieObject) {
        return (cookieObject.name === name);
      });
  }

  onSaveToClick(e) {
    var $link = $(e.currentTarget);
    this.postDataSaveToMyProjects.savedItemType = $link.data('type');
    this.postDataSaveToMyProjects.savedItemGtmLabel = $link.data('gtmLabel');
  }

  syncProductProjects(productId, linkProjectsCollection) {
    this.postDataSaveToMyProjects.savedItem = productId;
    if (linkProjectsCollection.length) {
      this.postDataSaveToMyProjects.projectName = this.getCsvFromProjectCollection(linkProjectsCollection.models, 'selected', 'name');
      this.postDataSaveToMyProjects.projectId = this.getCsvFromProjectCollection(linkProjectsCollection.models, 'selected', 'id');
    }
    this.onViewTracking(config.urls.marketingTracking.saveToMyProjects,this.postDataSaveToMyProjects);
  }

  syncDocumentProjects(documentUri, linkProjectsCollection) {
    this.postDataSaveToMyProjects.savedItem = documentUri;
    if (linkProjectsCollection.length) {
      this.postDataSaveToMyProjects.projectName = this.getCsvFromProjectCollection(linkProjectsCollection.models, 'selected', 'name');
      this.postDataSaveToMyProjects.projectId = this.getCsvFromProjectCollection(linkProjectsCollection.models, 'selected', 'id');
    }
    this.onViewTracking(config.urls.marketingTracking.saveToMyProjects,this.postDataSaveToMyProjects);
  }

  getCsvFromProjectCollection(linkProjectsCollectionArray, filterPropertyName, propertyToConcatenate) {
    let csvString = _.reduce(_.filter(linkProjectsCollectionArray, function(x) { return x.get(filterPropertyName); }), function(ini, y) { return ini + '' + y.get(propertyToConcatenate) + ',' ; }, '');
    csvString = csvString.substring(0, csvString.length - 1);
    return csvString;
  }

  collectUserData(userEmail) {
    let profileDataLayer = window.dataLayer && window.dataLayer.filter((item) => item.user);
    let pageInfoDataLayer = window.dataLayer && window.dataLayer.filter((item) => item.page && item.page.pageInfo && item.page.pageInfo.apiRegion);
    let profileData = null;
    let uData = this.splitKeyValue(cookies.get('uData'));
    if (profileDataLayer && profileDataLayer.length > 0) {
      profileData = profileDataLayer[0].user.profile;
    }
    let userId = -1;
    if (profileData && profileData.userId && profileData.userId !== '-1' && profileData.userId !== -1) {
      userId = profileData.userId;
    } else if (uData && uData.uId && uData.uId !== '-1' && uData.uId !== -1) {
      userId = uData.uId;
    }
    let country = null;
    if (profileData && profileData.userGeo) {
      country = profileData.userGeo;
    }
    let preferredLanguage = cookies.get(config.cookies.arrowLanguage);
    let userAnonymousIdGids = this.readMultipleCookies(config.cookies.marketingTracking.userAnonymousIdGidCookie);
    let userAnonymousIdGAs = this.readMultipleCookies(config.cookies.marketingTracking.userAnonymousIdGACookie);
    let region = pageInfoDataLayer && pageInfoDataLayer[0] && pageInfoDataLayer[0].page.pageInfo.apiRegion;
    let result = {
      userEmail,
      userId,
      userAnonymousIdGids: userAnonymousIdGids.map(cookie => cookie.value),
      userAnonymousIdGAs: userAnonymousIdGAs.map(cookie => cookie.value),
      preferredLanguage,
      country,
      region,
      pageUrl: window.location.href
    };
    return result;
  }

  getCompanyUserDetails() {
    let userData = this.collectUserData(this.userInfoData && this.userInfoData.email);
    let companyData = {
      company: this.userInfoData && this.userInfoData.company
    };
    let data = Object.assign({}, userData, companyData);
    return data;
  }

  getPromotionalCartInfo(cartData) {
    const entries = cartData.entries.map(cartEntry => {
      let promotionalCartEntry = {};
      promotionalCartEntry.catalogPartNo = cartEntry.catalogPartNo;
      promotionalCartEntry.catalogPartSeo = cartEntry.catalogPartSeo;
      promotionalCartEntry.partDescription = cartEntry.partDescription;
      promotionalCartEntry.smallImage = cartEntry.smallImage;
      promotionalCartEntry.productUrl = cartEntry.productUrl;
      return promotionalCartEntry;
    });
    return  {
      entries,
      cartItemCount: cartData.cartItemCount,
      viewCartUrl: cartData.viewCartUrl,
      total: cartData.total
    };
  }

  onAddToCart(cartData) {
    this.onViewTracking(config.urls.marketingTracking.trackAddToCart,cartData);
  }

  onSocialShare(shareData) {
    let clickShareEventHandler = (e) => {
      let className = e.target.className;
      socialShareClassMapping.every(mapping => {
        if (className.indexOf(mapping.className) !== -1) {
          // Send data to API
          let shareTrackData = Object.assign({}, shareData)
          shareTrackData.shareChannel =  mapping.shareChannel;
          delete shareTrackData.analyticsConfig;
          if (EnableAdobeAnalytics.isEnabled()) {
            window.adobeDataLayer.push({
              event: 'socialShare',
              network: mapping.shareChannel || undefined
            });
          }
          this.onViewTracking(config.urls.marketingTracking.trackSocialShare,shareTrackData);
          // Stop the loop
          return false;
        } else {
          return true;
        }
      });
    };
    $('body').on('click', '.Share-link', clickShareEventHandler);
    mediator.once(config.events.modal.closing, () => {
      // Removing the click handler after a short delay
      _.delay(
        () => $('body').off('click', '.Share-link', clickShareEventHandler),
        500);
    });
  }

  onDatasheetsEvent(datasheetsData) {
    this.onViewTracking(config.urls.marketingTracking.trackDatasheet,datasheetsData);
  }

  onReferenceDesignsEvent(referenceDesignsData) {
    this.onViewTracking(config.urls.marketingTracking.trackReferenceDesign,referenceDesignsData);
  }

  onManufacturerEvent(manufacturerData) {
    this.onViewTracking(config.urls.marketingTracking.trackManufacturer,manufacturerData);
  }

  onViewArticlesAndEvents(modelData) {
    this.onViewTracking(config.urls.marketingTracking.trackArticlesAndEvents,modelData);
  }

  async onViewTracking(url, trackingData) {
    if (this.isSubscribedForAuthenticatedTracking()) {
      //Defensive check and get from session if user authenticated
      //OR TODO: Trigger config.events.cart.userInfoSet from CartUserService.getUserInfo()
      if (this.isUserAuthenticated() && this.userInfoData === undefined) {
        let currentUser;
        if (EnableB2BUsersApiCall.isEnabled()) {
          currentUser = await cartApi.getLoyaltyUser();
          this.userInfo(currentUser);
        } else {
          currentUser = utilities.sessionStorageLoad('b2busers');
          if (currentUser) {
            this.userInfo(currentUser);
          }
        }
      }
      let userData = this.collectUserData(this.userInfoData && this.userInfoData.email);
      let data = Object.assign({}, userData, trackingData);
      this.track(url, data);
    }
  }

  track(urlPostfix, data) {
    $.ajax({
      type: 'POST',
      url: config.urls.marketingTracking.apiUrl + urlPostfix,
      data: JSON.stringify(data),
      contentType: 'application/json',
      processData: false,
      global: false
    })
  }
}

new MarketingTracking();



// WEBPACK FOOTER //
// ./assets/components/MarketingTracking/MarketingTracking.js